<template>
    <div class="col-md-12">
        <div class="card">
            <!-- Que se exporta: departamentos -> TPV -> aperturas de caja (+ QR) -->
            <div class="row">

                <!-- Departamentos (multi) y "todos" -->
                <div class="col-md-3">
                    <div class="form-group">
                        <label>{{ $t("evDepartamentos") }}</label>
                        <el-select v-model="localBusinesses" multiple collapse-tags class="cb-select"
                            :disabled="localTodos" :placeholder="$t('evEligeDepartamentos')"
                            @change="onBusinessesChange">
                            <el-option v-for="dep in arbol" :key="'dep-' + dep.idBusiness" :value="dep.idBusiness"
                                :label="etiquetaDepartamento(dep)" />
                        </el-select>
                        <label class="cb-check">
                            <input type="checkbox" v-model="localTodos" @change="onTodosChange" />
                            {{ $t("evTodosLosDepartamentos") }}
                        </label>
                    </div>
                </div>

                <!-- TPV de los departamentos elegidos (multi) -->
                <div class="col-md-3">
                    <div class="form-group">
                        <label>{{ $t("evTpvsAExportar") }}</label>
                        <el-select v-model="localTpvs" multiple collapse-tags class="cb-select"
                            :placeholder="$t('evEligeTpv')" @change="onTpvsChange">
                            <el-option-group v-for="dep in departamentosElegidos" :key="'g-' + dep.idBusiness"
                                :label="dep.nombre">
                                <el-option v-for="tpv in dep.tpvs" :key="'tpv-' + tpv.idTpv" :value="tpv.idTpv"
                                    :label="etiquetaTpv(tpv)" />
                            </el-option-group>
                        </el-select>
                        <small class="cb-ayuda text-muted">{{ $t("evTpvsAyuda") }}</small>
                    </div>
                </div>

                <!-- Aperturas: varias solo con un unico TPV; con varios, la mas antigua de cada uno -->
                <div class="col-md-3">
                    <div class="form-group">
                        <label>{{ $t("evAperturasAExportar") }}</label>
                        <el-select v-if="tpvUnico" v-model="localDays" multiple collapse-tags class="cb-select"
                            :placeholder="$t('evAperturaMasAntigua')" @change="onDaysChange">
                            <el-option v-for="ap in tpvUnico.aperturas" :key="'ap-' + ap.idDay" :value="ap.idDay"
                                :label="etiquetaApertura(ap)" />
                        </el-select>
                        <input v-else type="text" class="form-control" disabled
                            :value="localTpvs.length ? $t('evAperturaMasAntiguaCadaTpv') : $t('evEligeTpv')" />
                        <label class="cb-check">
                            <input type="checkbox" v-model="localIncluirQR" @change="emitirSeleccion" />
                            {{ $t("evIncluirFacturasQR") }} <span class="cb-num">{{ totalQR }}</span>
                        </label>
                    </div>
                </div>

                <!-- La lista se carga al pulsar, no al cambiar los selectores -->
                <div class="col-md-3">
                    <div class="form-group">
                        <label>&nbsp;</label>
                        <button type="button" class="btn cb-btn-cargar" :class="{ 'cb-btn-cargar-pendiente': cambiosPendientes }"
                            :disabled="cargando || !localTpvs.length" @click="$emit('aplicar')">
                            <i class="fas" :class="cargando ? 'fa-spinner fa-spin' : 'fa-sync-alt'"></i>
                            {{ cambiosPendientes ? $t("evActualizarVentas") : $t("evCargarVentas") }}
                        </button>
                        <small v-if="cambiosPendientes" class="cb-ayuda cb-ayuda-aviso">
                            <i class="fas fa-exclamation-triangle"></i> {{ $t("evSeleccionCambiada") }}
                        </small>
                        <!-- Los Excel de tandas anteriores, sin tener que exportar -->
                        <a class="cb-enlace-informes" @click="$emit('informes')">
                            <i class="fas fa-file-excel"></i> {{ $t("evInformesExportacion") }}
                        </a>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import { Select, Option, OptionGroup } from "element-ui";

export default {
    components: {
        [Select.name]: Select,
        [Option.name]: Option,
        [OptionGroup.name]: OptionGroup,
    },

    props: {
        // Departamentos -> TPV -> aperturas pendientes (getAperturasPendientes)
        arbol: {
            type: Array,
            default: () => []
        },
        // { todos, idBusinesses, idTpvs, idDays, incluirQR }
        seleccion: {
            type: Object,
            default: () => ({ todos: false, idBusinesses: [], idTpvs: [], idDays: [], incluirQR: true })
        },
        // La seleccion ha cambiado desde la ultima carga de la lista
        cambiosPendientes: {
            type: Boolean,
            default: false
        },
        cargando: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            localTodos: !!this.seleccion.todos,
            localBusinesses: (this.seleccion.idBusinesses || []).slice(),
            localTpvs: (this.seleccion.idTpvs || []).slice(),
            localDays: (this.seleccion.idDays || []).slice(),
            localIncluirQR: this.seleccion.incluirQR !== false,
            // Lo elegido antes de marcar "Todos los departamentos", para poder volver
            seleccionAntesDeTodos: null,
        };
    },

    computed: {
        departamentosElegidos() {
            return this.arbol.filter(dep => this.localBusinesses.indexOf(dep.idBusiness) !== -1);
        },

        tpvsElegidos() {
            const tpvs = [];
            this.departamentosElegidos.forEach(dep => {
                (dep.tpvs || []).forEach(tpv => {
                    if (this.localTpvs.indexOf(tpv.idTpv) !== -1) tpvs.push(tpv);
                });
            });
            return tpvs;
        },

        /* Solo con un TPV se pueden elegir varias aperturas */
        tpvUnico() {
            return this.tpvsElegidos.length === 1 ? this.tpvsElegidos[0] : null;
        },

        totalQR() {
            return this.tpvsElegidos.reduce((s, tpv) => s + ((tpv.qr && tpv.qr.aExportar) || 0), 0);
        },
    },

    watch: {
        seleccion: {
            deep: true,
            handler(nueva) {
                this.localTodos = !!nueva.todos;
                this.localBusinesses = (nueva.idBusinesses || []).slice();
                this.localTpvs = (nueva.idTpvs || []).slice();
                this.localDays = (nueva.idDays || []).slice();
                this.localIncluirQR = nueva.incluirQR !== false;
            }
        },
    },

    methods: {
        pendientesTpv(tpv) {
            const aperturas = (tpv.aperturas || []).reduce((s, ap) => s + (ap.aExportar || 0), 0);
            return aperturas + ((tpv.qr && tpv.qr.aExportar) || 0);
        },

        pendientesDepartamento(dep) {
            return (dep.tpvs || []).reduce((s, tpv) => s + this.pendientesTpv(tpv), 0);
        },

        etiquetaDepartamento(dep) {
            return dep.nombre + ' (' + this.pendientesDepartamento(dep) + ')';
        },

        etiquetaTpv(tpv) {
            return tpv.nombre + ' (' + this.pendientesTpv(tpv) + ')';
        },

        etiquetaApertura(ap) {
            let texto = ap.apertura + ' · ' + ap.aExportar + ' ' + this.$t('evPendientesCorto');
            const extras = [];
            if (ap.rechazados) extras.push(ap.rechazados + ' ' + this.$t('evRechazadosCorto'));
            if (ap.omitidos) extras.push(ap.omitidos + ' ' + this.$t('evOmitidosCorto'));
            if (ap.excluidos) extras.push(ap.excluidos + ' ' + this.$t('evExcluidosCorto'));
            if (extras.length) texto += ' (' + extras.join(', ') + ')';
            return texto;
        },

        todosLosTpvsDe(idsBusinesses) {
            const ids = [];
            this.arbol.forEach(dep => {
                if (idsBusinesses.indexOf(dep.idBusiness) === -1) return;
                (dep.tpvs || []).forEach(tpv => ids.push(tpv.idTpv));
            });
            return ids;
        },

        /* Marcar "todos" guarda lo que habia elegido; desmarcarlo lo devuelve */
        onTodosChange() {
            if (this.localTodos) {
                this.seleccionAntesDeTodos = {
                    idBusinesses: this.localBusinesses.slice(),
                    idTpvs: this.localTpvs.slice(),
                    idDays: this.localDays.slice(),
                };
                this.localBusinesses = this.arbol.map(dep => dep.idBusiness);
                this.localTpvs = this.todosLosTpvsDe(this.localBusinesses);
                this.localDays = [];
            } else if (this.seleccionAntesDeTodos) {
                this.localBusinesses = this.seleccionAntesDeTodos.idBusinesses;
                this.localTpvs = this.seleccionAntesDeTodos.idTpvs;
                this.localDays = this.seleccionAntesDeTodos.idDays;
                this.seleccionAntesDeTodos = null;
            } else {
                // Sin seleccion previa que recuperar (arranque con "todos"), se queda
                // solo el primer departamento con sus TPV
                const primero = this.localBusinesses.length
                    ? this.localBusinesses[0]
                    : (this.arbol[0] ? this.arbol[0].idBusiness : null);
                this.localBusinesses = primero !== null ? [primero] : [];
                this.localTpvs = this.todosLosTpvsDe(this.localBusinesses);
                this.localDays = [];
            }

            this.emitirSeleccion();
        },

        onBusinessesChange() {
            // Al cambiar de departamentos se vuelve a "todos sus TPV, apertura mas antigua"
            this.localTpvs = this.todosLosTpvsDe(this.localBusinesses);
            this.localDays = [];
            this.emitirSeleccion();
        },

        onTpvsChange() {
            this.localDays = [];
            this.emitirSeleccion();
        },

        onDaysChange() {
            this.emitirSeleccion();
        },

        emitirSeleccion() {
            this.$emit('seleccionChanged', {
                todos: this.localTodos,
                idBusinesses: this.localBusinesses.slice(),
                idTpvs: this.localTpvs.slice(),
                idDays: this.localDays.slice(),
                incluirQR: this.localIncluirQR,
            });
        },
    },
};
</script>

<style scoped>
.card {
    padding: 20px;
    margin-bottom: 15px;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: #fff;
}

.form-group label {
    font-weight: bold;
}

.cb-select {
    width: 100%;
}

.cb-check {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 6px 0 0;
    font-weight: normal !important;
    font-size: 13px;
    cursor: pointer;
}

.cb-num {
    background: #eef1f7;
    border-radius: 10px;
    padding: 0 7px;
    font-size: 12px;
}

.cb-ayuda {
    display: block;
    margin-top: 6px;
    font-size: 12px;
}

.cb-ayuda-aviso {
    color: #8a5a00;
}

.cb-enlace-informes {
    display: block;
    margin-top: 6px;
    font-size: 12px;
    text-align: right;
    color: #1d6f42;
    cursor: pointer;
}

.cb-enlace-informes:hover {
    text-decoration: underline;
}

/* El tema pone el texto en blanco y fondo de color al pasar el raton por el el-select */
.cb-select /deep/ .el-input:hover input {
    color: #4b5563 !important;
    background-color: #fff !important;
}

/* La flecha gira con su caja: sin fondo, o se ve el cuadro girando */
.cb-select /deep/ .el-input:hover .el-input__icon,
.cb-select /deep/ .el-input:hover .el-input__icon i {
    color: #4b5563 !important;
    background-color: transparent !important;
}

.cb-select /deep/ .el-input:hover input::placeholder {
    color: #9aa0a6 !important;
}

/* Etiquetas de lo seleccionado: fondo tenue y borde del color de la app */
.cb-select /deep/ .el-select__tags .el-tag {
    background-color: #eceef8 !important;
    border: 1px solid #101255 !important;
    color: #101255 !important;
    font-weight: 600;
    margin-left: 4px;
    margin-bottom: 2px;
}

.cb-select /deep/ .el-select__tags .el-tag .el-tag__close {
    color: #101255 !important;
    background-color: transparent !important;
    opacity: 1;
}

.cb-select /deep/ .el-select__tags .el-tag .el-tag__close:hover {
    color: #b42318 !important;
}

/* Misma altura que los el-select (40px) para que quede alineado con ellos */
.cb-btn-cargar {
    width: 100%;
    height: 40px;
    padding: 0 16px;
    line-height: 38px;
    background: #101255;
    border-color: #101255;
    color: #fff;
}

.cb-btn-cargar:disabled {
    opacity: 0.6;
}

.cb-btn-cargar-pendiente {
    background: #c77700;
    border-color: #c77700;
}
</style>



// WEBPACK FOOTER //
// src/components/CabeceraBusinessCentral/CabeceraBusinessCentral.vue